import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { queryKeys } from '@/api/queryKeys';
import { WebhookDeliveryStatus, WebhookEvent } from '@/api/generated-types';
import type { PageOfWebhookDeliveryListItem } from '@/api/types';
import { toastApiError } from '@/components/Notifications/apiErrorToast';
import { isNotFoundError } from '@/utils/errors/apiErrors';
import {
  getTeamSettings,
  getTeamModelAssignments,
  createTeamSettings,
  updateTeamSettings,
  deleteTeamSettings,
  updateTeamModelSelection,
  testWebhook,
  getWebhookDeliveries,
  getWebhookDeliveryDetail,
  retryWebhookDelivery,
} from './api';
import type {
  CreateTeamSettingsRequest,
  UpdateTeamSettingsRequest,
  UpdateTeamModelSelectionRequest,
  WebhookTestRequest,
} from '@/types/teamTypes';

export function useTeamSettings(teamId: string) {
  return useQuery({
    queryKey: queryKeys.teams.settings(teamId),
    queryFn: () => getTeamSettings(teamId),
    enabled: !!teamId,
    // A 404 means the team has no settings row yet — a terminal state, not worth retrying.
    retry: (failureCount, error) => !isNotFoundError(error) && failureCount < 1,
  });
}

export function useTeamModelAssignments() {
  return useQuery({
    queryKey: queryKeys.teams.modelAssignments(),
    queryFn: getTeamModelAssignments,
  });
}

export function useCreateTeamSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      teamId,
      settingsData,
    }: {
      teamId: string;
      settingsData: CreateTeamSettingsRequest;
    }) => createTeamSettings(teamId, settingsData),
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.settings(teamId),
      });
    },
    onError: (error) => toastApiError(error, 'Failed to create team settings'),
  });
}

export function useUpdateTeamSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      teamId,
      settingsData,
    }: {
      teamId: string;
      settingsData: UpdateTeamSettingsRequest;
    }) => updateTeamSettings(teamId, settingsData),
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.settings(teamId),
      });
    },
    onError: (error) => toastApiError(error, 'Failed to update team settings'),
  });
}

export function useDeleteTeamSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTeamSettings,
    onSuccess: (_, teamId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.settings(teamId),
      });
    },
    onError: (error) => toastApiError(error, 'Failed to delete team settings'),
  });
}

export function useTeamSettingsSave(teamId: string, isNewSettings: boolean) {
  const create = useCreateTeamSettings();
  const update = useUpdateTeamSettings();

  const save = useCallback(
    async (partial: CreateTeamSettingsRequest, label: string) => {
      if (!teamId) return;
      const mutation = isNewSettings ? create : update;
      await mutation.mutateAsync({ teamId, settingsData: partial });
      toast.success(
        isNewSettings ? 'Team settings created successfully' : `${label} saved successfully`
      );
    },
    [teamId, isNewSettings, create, update]
  );

  return { save, isSaving: create.isPending || update.isPending };
}

export function useUpdateTeamModelSelection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      teamId,
      request,
    }: {
      teamId: string;
      request: UpdateTeamModelSelectionRequest;
    }) => updateTeamModelSelection(teamId, request),
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.settings(teamId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.modelAssignments() });
      toast.success('Team model selection updated');
    },
    onError: (error) => toastApiError(error, 'Failed to update model selection'),
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: ({ teamId, request }: { teamId: string; request?: WebhookTestRequest }) =>
      testWebhook(teamId, request),
    onError: (error) => toastApiError(error, 'Webhook test failed'),
  });
}

export function useWebhookDeliveries(
  teamId: string,
  page: number,
  pageSize: number,
  status?: string,
  eventType?: string,
  transactionId?: string
) {
  return useQuery({
    queryKey: queryKeys.teams.deliveries.list(
      teamId,
      page,
      pageSize,
      status,
      eventType,
      transactionId
    ),
    queryFn: () => getWebhookDeliveries(teamId, page, pageSize, status, eventType, transactionId),
    enabled: !!teamId,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
}

const DELIVERY_POLL_MS = 10_000;
const DELIVERY_POLL_LIMIT_MS = 2 * 60 * 1000;
const EXPORT_DELIVERY_PAGE_SIZE = 1;

const latestExportDelivery = (page: PageOfWebhookDeliveryListItem | undefined) =>
  page?.items[0] ?? null;

export function useExportDelivery(teamId: string, transactionId: string | null, enabled: boolean) {
  const pageArgs = [
    teamId,
    1,
    EXPORT_DELIVERY_PAGE_SIZE,
    undefined,
    WebhookEvent.DocumentExported,
    transactionId ?? '',
  ] as const;

  return useQuery({
    queryKey: queryKeys.teams.deliveries.list(...pageArgs),
    queryFn: () => getWebhookDeliveries(...pageArgs),
    enabled: enabled && !!teamId && !!transactionId,
    select: latestExportDelivery,
    refetchInterval: (query) => {
      const row = latestExportDelivery(query.state.data);
      if (!row || row.status === WebhookDeliveryStatus.Delivered) return false;
      return Date.now() >= Date.parse(row.createdAt) + DELIVERY_POLL_LIMIT_MS
        ? false
        : DELIVERY_POLL_MS;
    },
  });
}

export function useWebhookDeliveryDetail(teamId: string, deliveryId: string | null) {
  return useQuery({
    queryKey: queryKeys.teams.deliveries.detail(teamId, deliveryId ?? ''),
    queryFn: () => getWebhookDeliveryDetail(teamId, deliveryId!),
    enabled: !!teamId && !!deliveryId,
    staleTime: 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === WebhookDeliveryStatus.Pending || status === WebhookDeliveryStatus.Delivering
        ? DELIVERY_POLL_MS
        : false;
    },
  });
}

export function useRetryWebhookDelivery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, deliveryId }: { teamId: string; deliveryId: string }) =>
      retryWebhookDelivery(teamId, deliveryId),
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.deliveries.all(teamId),
      });
    },
    onError: (error) => toastApiError(error, 'Failed to retry delivery'),
  });
}
